import { AICC_SANDBOX_MODE, AICC_SANDBOX_SEED_MESSAGE, AiccSandboxMode } from "constants/aiccPromptSandbox";
import {
  AiccSandboxGrading,
  AiccSandboxGrammarResult,
  AiccSandboxTurn,
  AiccSandboxTurnResult,
} from "types/model/aiccPromptSandbox";

// 🔴 Bubbles are not the transcript: the seed turn and the appended wrap-up instruction are replayed but never shown.
export interface SandboxBubble {
  role: "user" | "assistant";
  text: string;
  nativeText?: string;
  translation?: string;
  transliteration?: string;
  grading?: AiccSandboxGrading;
  grammar?: AiccSandboxGrammarResult;
  // 🔴 Graded with no `grammar` means the check was skipped on a phrase match, not that it found nothing.
  isGraded?: boolean;
}

export interface SandboxConversation {
  transcript: AiccSandboxTurn[];
  bubbles: SandboxBubble[];
  isEnded: boolean;
}

export const EMPTY_CONVERSATION: SandboxConversation = { transcript: [], bubbles: [], isEnded: false };

// 🔴 Onboarding's greeting is the turn's `reply`, not `opener`; reading only `opener` rendered an empty bubble.
export const openedConversation = (result: AiccSandboxTurnResult, mode: AiccSandboxMode): SandboxConversation => {
  const { opener } = result;
  const text = (opener ? opener.text : result.reply) ?? "";

  // 🔴 No seed turn: a `[user:"start"]` here would be graded as the learner's first answer.
  if (mode === AICC_SANDBOX_MODE.lessonReview) {
    return {
      transcript: [{ role: "assistant", content: text }],
      bubbles: [{ role: "assistant", text, transliteration: result.transliteration }],
      isEnded: result.isEnded,
    };
  }

  return {
    transcript: [
      { role: "user", content: opener ? AICC_SANDBOX_SEED_MESSAGE : "" },
      { role: "assistant", content: text },
    ],
    bubbles: [
      {
        role: "assistant",
        text,
        translation: opener ? opener.translation : result.translation,
        transliteration: opener ? opener.transliteration : result.transliteration,
      },
    ],
    isEnded: result.isEnded,
  };
};

// 🔴 The transcript keeps `historyUserContent`: production replays the appended wrap-up instruction every round.
export const continuedConversation = (
  current: SandboxConversation,
  typed: string,
  result: AiccSandboxTurnResult
): SandboxConversation => ({
  transcript: [
    ...current.transcript,
    { role: "user", content: result.historyUserContent || typed },
    { role: "assistant", content: result.reply },
  ],
  bubbles: [
    ...current.bubbles,
    {
      role: "user",
      text: typed,
      nativeText: result.userNativeText,
      grammar: result.grammar,
      ...(result.grading ? { isGraded: true } : {}),
    },
    {
      role: "assistant",
      text: result.reply,
      translation: result.translation,
      transliteration: result.transliteration,
      grading: result.grading,
    },
  ],
  isEnded: result.isEnded,
});
